import React, { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Paperclip, FileText, Camera, SprayCan } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, TextArea, Button } from "@/components/ui-kit";
import DocViewer from "@/components/DocViewer";
import { listCleaningProducts, createCleaningProduct, updateCleaningProduct, deleteCleaningProduct, uploadFile } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { getUser } from "@/lib/auth";

const empty = { id: null, name: "", provider: "", use: "", dosage: "", zone: "", datasheets: [], notes: "" };

export default function CleaningProducts() {
  const isAdmin = getUser()?.rol === "admin";
  const { data: products, mutate } = useSWR("cleaning-products", listCleaningProducts);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);

  const startNew = () => setEditing({ ...empty });
  const startEdit = (p) => setEditing({ id: p.id, name: p.name, provider: p.provider || "", use: p.use || "", dosage: p.dosage || "", zone: p.zone || "", datasheets: [...(p.datasheets || [])], notes: p.notes || "" });

  const handleFt = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const f = await resizeImage(file);
        const { url } = await uploadFile(f);
        setEditing((s) => ({ ...s, datasheets: [...s.datasheets, url] }));
      }
    } catch { toast.error("No s'ha pogut pujar el document"); }
    finally { setUploading(false); }
  };
  const removeDoc = (url) => setEditing((s) => ({ ...s, datasheets: s.datasheets.filter((u) => u !== url) }));

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Posa un nom");
    setSaving(true);
    try {
      const payload = { name: editing.name.trim(), provider: editing.provider, use: editing.use, dosage: editing.dosage, zone: editing.zone, datasheets: editing.datasheets, notes: editing.notes, active: true };
      if (editing.id) await updateCleaningProduct(editing.id, payload);
      else await createCleaningProduct(payload);
      toast.success("Producte desat"); setEditing(null); mutate();
    } catch (err) { toast.error(err.response?.data?.detail || "Error desant"); }
    finally { setSaving(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Eliminar "${p.name}"?`)) return;
    try { await deleteCleaningProduct(p.id); toast.success("Eliminat"); mutate(); }
    catch { toast.error("Error eliminant"); }
  };

  return (
    <>
      <Header title="Productes de neteja" />
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex items-center justify-between gap-3">
          <div>
            <div className="label-eyebrow">Pla 2 · Neteja i desinfecció</div>
            <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Productes i fitxes tècniques</h2>
          </div>
          {isAdmin && !editing && <Button onClick={startNew} testid="cleaning-new"><Plus size={18} /> Nou</Button>}
        </div>
        <p className="mt-2 text-sm text-ink-soft">Fitxes tècniques dels productes de neteja, accessibles per a inspecció.</p>

        {editing && (
          <Card className="mt-6"><div className="space-y-4">
            <div><div className="label-eyebrow mb-2">Nom del producte</div>
              <TextInput value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} placeholder="Ex: Sanytol Desgreixant" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="label-eyebrow mb-2">Proveïdor</div>
                <TextInput value={editing.provider} onChange={(e) => setEditing((s) => ({ ...s, provider: e.target.value }))} /></div>
              <div><div className="label-eyebrow mb-2">Zona d'ús</div>
                <TextInput value={editing.zone} onChange={(e) => setEditing((s) => ({ ...s, zone: e.target.value }))} placeholder="Obrador · Botiga" /></div>
            </div>
            <div><div className="label-eyebrow mb-2">Ús</div>
              <TextInput value={editing.use} onChange={(e) => setEditing((s) => ({ ...s, use: e.target.value }))} placeholder="Ex: Desgreixant desinfectant" /></div>
            <div><div className="label-eyebrow mb-2">Dosificació</div>
              <TextInput value={editing.dosage} onChange={(e) => setEditing((s) => ({ ...s, dosage: e.target.value }))} placeholder="Ex: dilució 1:10" /></div>
            <div>
              <div className="label-eyebrow mb-2">Fitxa tècnica / seguretat (PDF o foto)</div>
              <div className="space-y-2">
                {editing.datasheets.map((url) => (
                  <div key={url} className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2">
                    <FileText size={16} className="shrink-0 text-ink-soft" />
                    <button type="button" onClick={() => setViewer(url)} className="flex-1 truncate text-left text-sm text-terracotta underline">{url.split("/").pop()}</button>
                    <button type="button" onClick={() => removeDoc(url)} className="shrink-0 text-ink-soft hover:text-ember" aria-label="Treure"><X size={16} /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white px-3 py-3 text-sm font-semibold text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                    <Camera size={16} /> {uploading ? "Pujant…" : "Càmera"}
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFt} disabled={uploading} />
                  </label>
                  <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white px-3 py-3 text-sm font-semibold text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                    <Paperclip size={16} /> Document
                    <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleFt} disabled={uploading} />
                  </label>
                </div>
              </div>
            </div>
            <div><div className="label-eyebrow mb-2">Notes</div>
              <TextArea value={editing.notes} onChange={(e) => setEditing((s) => ({ ...s, notes: e.target.value }))} /></div>
            <div className="flex gap-3">
              <Button onClick={save} disabled={saving || uploading} testid="cleaning-save"><Check size={18} /> {saving ? "Desant…" : "Desar"}</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}><X size={18} /> Cancel·lar</Button>
            </div>
          </div></Card>
        )}

        <div className="mt-6 space-y-3">
          {products?.length === 0 && !editing && <Card className="text-center text-sm text-ink-soft">Encara no hi ha productes de neteja.</Card>}
          {products?.map((p) => (
            <Card key={p.id} testid={`cleaning-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-forest-bg text-forest"><SprayCan size={16} /></span>
                    <div className="font-display text-lg font-bold text-ink">{p.name}</div>
                  </div>
                  {p.use && <div className="mt-1 text-sm text-ink-soft">{p.use}</div>}
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-ink-soft">
                    {p.zone && <span className="rounded-full bg-cream px-2 py-0.5 font-semibold text-ink">{p.zone}</span>}
                    {p.dosage && <span>· {p.dosage}</span>}
                    {p.provider && <span>· {p.provider}</span>}
                  </div>
                  {p.datasheets?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {p.datasheets.map((url) => (
                        <button key={url} type="button" onClick={() => setViewer(url)}
                          className="inline-flex items-center gap-1 rounded-full bg-terracotta/10 px-3 py-1 text-xs font-semibold text-terracotta hover:bg-terracotta/20">
                          <FileText size={12} /> Veure FT
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(p)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-ink" aria-label="Editar"><Pencil size={16} /></button>
                    <button onClick={() => remove(p)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-ember-bg hover:text-ember" aria-label="Eliminar"><Trash2 size={16} /></button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
      {viewer && <DocViewer url={viewer} onClose={() => setViewer(null)} />}
    </>
  );
}
